import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Tooltip } from 'src/domains/players/webview/components/Account/summaryTab/accountSummary/Tooltip/Tooltip';
import {
    CalculationTypeLabel,
    CalculationTypeRow,
    CopyHeading,
    CopyParagraph,
    CopyRoot,
    CopyTerm,
    ExplanationPanel,
    ShowExplanationButton,
    ShowExplanationChevron,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsCopy.style';

const LimitTypeDefinitions = observer('LimitTypeDefinitions', (): JSX.Element => {
    return (
        <>
            <CopyParagraph data-test='deposit-limits-copy-gross-definition'>
                <CopyTerm>
                    <I18n
                        langKey='account.deposit-limits-edit.deposit-limit.label'
                        defaultText='Deposit Limit:'
                    />
                </CopyTerm>{' '}
                <I18n
                    langKey='account.deposit-limits-edit.deposit-limit.body'
                    defaultText='Calculated strictly on a calendar basis, tracking the total amount of your deposits over the specified calendar period.'
                />
            </CopyParagraph>
            <CopyParagraph data-test='deposit-limits-copy-net-definition'>
                <CopyTerm>
                    <I18n
                        langKey='account.deposit-limits-edit.net-deposit-limit.label'
                        defaultText='Net Deposit Limit:'
                    />
                </CopyTerm>{' '}
                <I18n
                    langKey='account.deposit-limits-edit.net-deposit-limit.body'
                    defaultText='Calculated on either a rolling or calendar basis, taking into account both your deposits and withdrawals over your chosen period.'
                />
            </CopyParagraph>
        </>
    );
});

const CalculationTypesSection = observer('CalculationTypesSection', (): JSX.Element => {
    return (
        <>
            <CopyHeading data-test='deposit-limits-copy-calculation-types-title'>
                <I18n
                    langKey='account.deposit-limits-edit.calculation-types.title'
                    defaultText='Understanding Calculation Types:'
                />
            </CopyHeading>
            <CalculationTypeRow data-test='deposit-limits-copy-rolling-period'>
                <CalculationTypeLabel>
                    <I18n
                        langKey='account.deposit-limits-edit.calculation-types.rolling.label'
                        defaultText='Rolling Period'
                    />
                </CalculationTypeLabel>
                <Tooltip
                    portal={true}
                    type='info'
                    dataTest='deposit-limits-rolling-period-tooltip'
                    content={
                        <I18n
                            langKey='account.deposit-limits-edit.calculation-types.rolling.tooltip'
                            defaultText='Continuously recalculates your limits dynamically based on your activity over the exact preceding timeframe (e.g., the last 24 hours for a 1-day limit, or the past 7 days).'
                        />
                    }
                />
            </CalculationTypeRow>
            <CalculationTypeRow data-test='deposit-limits-copy-calendar-period'>
                <CalculationTypeLabel>
                    <I18n
                        langKey='account.deposit-limits-edit.calculation-types.calendar.label'
                        defaultText='Calendar Period'
                    />
                </CalculationTypeLabel>
                <Tooltip
                    portal={true}
                    type='info'
                    dataTest='deposit-limits-calendar-period-tooltip'
                    content={
                        <I18n
                            langKey='account.deposit-limits-edit.calculation-types.calendar.tooltip'
                            defaultText='Resets at fixed intervals according to the calendar (e.g., daily limits reset at midnight, weekly limits reset every Monday, and monthly limits reset on the first of the month).'
                        />
                    }
                />
            </CalculationTypeRow>
        </>
    );
});

const ShowExplanationToggle = observer(
    'ShowExplanationToggle',
    ({ isOpen, onToggle, dataTest }: { isOpen: boolean; onToggle: () => void; dataTest: string }): JSX.Element => {
        return (
            <ShowExplanationButton
                type='button'
                aria-expanded={isOpen}
                onClick={onToggle}
                data-test={dataTest}
            >
                <ShowExplanationChevron position={isOpen === true ? 'up' : 'down'} />
                {isOpen === true ? (
                    <I18n
                        langKey='account.deposit-limits-edit.hide-explanation'
                        defaultText='Hide explanation'
                    />
                ) : (
                    <I18n
                        langKey='account.deposit-limits-edit.show-explanation'
                        defaultText='Show explanation'
                    />
                )}
            </ShowExplanationButton>
        );
    }
);

interface EditLimitsDescriptionPropsType {
    dataTest: string;
}

export const EditLimitsDescription = observer(
    'EditLimitsDescription',
    ({ dataTest }: EditLimitsDescriptionPropsType): JSX.Element => {
        const [isExplanationOpen, setIsExplanationOpen] = useState(false);

        return (
            <CopyRoot data-test={dataTest}>
                <CopyParagraph data-test='deposit-limits-edit-updating'>
                    <CopyTerm>
                        <I18n
                            langKey='account.deposit-limits-edit.instructions.updating-label'
                            defaultText='Updating your limits:'
                        />
                    </CopyTerm>{' '}
                    <I18n
                        langKey='account.deposit-limits-edit.instructions.updating-body'
                        defaultText='Lowering your deposit limit takes effect immediately. Increasing your limit will trigger a mandatory 24h cooling-off period before it goes live.'
                    />
                </CopyParagraph>
                <ShowExplanationToggle
                    isOpen={isExplanationOpen}
                    dataTest='deposit-limits-show-explanation'
                    onToggle={(): void => {
                        setIsExplanationOpen((open) => !open);
                    }}
                />
                {isExplanationOpen === true ? (
                    <ExplanationPanel data-test='deposit-limits-explanation-panel'>
                        <LimitTypeDefinitions />
                        <CalculationTypesSection />
                    </ExplanationPanel>
                ) : null}
            </CopyRoot>
        );
    }
);

interface SignupLimitsDescriptionPropsType {
    dataTest: string;
}

export const SignupLimitsDescription = observer(
    'SignupLimitsDescription',
    ({ dataTest }: SignupLimitsDescriptionPropsType): JSX.Element => {
        const [isExplanationOpen, setIsExplanationOpen] = useState(false);

        return (
            <CopyRoot
                isSignup={true}
                data-test={dataTest}
            >
                <CopyParagraph data-test='signup-deposit-limits-period-intro'>
                    <I18n
                        langKey='signup-journey.deposit-limits.description.period-options'
                        defaultText='You can set a 1-day, 7-day, or 30-day or Daily, Weekly, Monthly deposit limit using your preferred calculation method:'
                    />
                </CopyParagraph>
                <ShowExplanationToggle
                    isOpen={isExplanationOpen}
                    dataTest='signup-deposit-limits-show-explanation'
                    onToggle={(): void => {
                        setIsExplanationOpen((open) => !open);
                    }}
                />
                {isExplanationOpen === true ? (
                    <ExplanationPanel data-test='signup-deposit-limits-explanation-panel'>
                        <LimitTypeDefinitions />
                        <CopyParagraph data-test='signup-deposit-limits-approach'>
                            <I18n
                                langKey='signup-journey.deposit-limits.description.approach'
                                defaultText='This approach ensures that you always stay within your pre-set limit based on your activity within the selected time frame.'
                            />
                        </CopyParagraph>
                        <CalculationTypesSection />
                        <CopyParagraph data-test='signup-deposit-limits-select-option'>
                            <I18n
                                langKey='signup-journey.deposit-limits.description.select-option'
                                defaultText='Select the option that suits you best and set your daily, weekly, or monthly boundaries.'
                            />{' '}
                            <Tooltip
                                portal={true}
                                type='info'
                                align='end'
                                dataTest='signup-deposit-limits-default-limit-tooltip'
                                content={
                                    <I18n
                                        langKey='signup-journey.deposit-limits.description.default-limit-tooltip'
                                        defaultText='If a default limit is already applied for you, it will appear in grey font in the background of the input field as a guide. You can lower it, but you cannot raise it.'
                                    />
                                }
                            />
                        </CopyParagraph>
                    </ExplanationPanel>
                ) : null}
            </CopyRoot>
        );
    }
);
